/**
 * Shared sport metadata for the public site — the homepage sports menu
 * and each /sports/:slug page both read from this one list, so adding a
 * sport or changing its copy only has to happen in one place.
 *
 * Emoji + doubles support are kept in sync with the in-app source of truth:
 * GamePlanTab.jsx's SPORTS list (emoji) and matchFormat.js's DOUBLES_SPORTS
 * (which sports can be logged as doubles — squash is singles-only).
 *
 * Content follows the same story as the homepage's HierarchyFlow stepper
 * (Season → Competitions → Game Plans), told per sport. SportStepper.jsx
 * renders `season`, `competition` and `matchCycle` as its three stages:
 *  - season: a fully user-defined period (any length, athlete-named). Its
 *    matches = everything logged in its date range, standalone matches
 *    included. All six Game Analytics tabs filter by Ongoing / Previous /
 *    All seasons (shipped 2026-10-30, default Ongoing). Goals + milestones
 *    (result/skill/other; active/achieved/not_achieved) — see
 *    docs_zenanalytics/10-SEASON-GOALS-MILESTONES-2026-09-29.md.
 *  - competition: league or tournament, optionally linked to a season;
 *    rounds grouped, own W/L record, goal badge (✓/✗) once reviewed, days
 *    remaining while ongoing. Matches carry a round label ("Quarterfinal",
 *    "Week 3"). Reviewed on goal verdict + performance / plan outcome /
 *    tactical execution / mental focus.
 *  - matchCycle: short written plan beforehand, then a result rated two
 *    ways: qualitative (post-match notes) and quantitative (Performance ★,
 *    Plan adherence ★, Serve/Return/Forehand/Backhand/Net play — "Short
 *    game" for squash and table tennis; see utils/sportLabels.js).
 * `example` objects are illustrative mock-ups of what the in-app view
 * shows, written per sport so the six pages don't read as find-replaced.
 *
 * Voice: player to player. Every page argues "ZenAnalytics fits how THIS
 * sport is actually played and organised" using the sport's own structure
 * (draws, box leagues, league nights, partners) — never a feature list with
 * the sport's name swapped in. Copy states what the record shows; it never
 * promises an outcome (the player draws the conclusions).
 * Per-sport page strings: stepperHeadline (stepper h2), gamePlanLead /
 * gamePlanText (stage 3), recordHeadline (Game Analytics h2).
 */

export const SPORTS = [
  {
    slug: 'tennis',
    heroImage: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&w=1920&q=80',
    heroPosition: 'center',
    value: 'tennis',
    name: 'Tennis',
    emoji: '🎾',
    doubles: true,
    tagline: 'Every draw, every ladder match, every tiebreak — your tennis season, kept the way you actually played it.',
    body: "ZenAnalytics gives competitive and club tennis players a real record to develop from, because tennis is never just one competition. There's the summer tournament run, the club ladder alongside it, singles on Saturday and doubles on Tuesday — and a season's memory flattens all of it into “went okay”. Here every match sits in the draw or league it belongs to, the third-set tiebreak stays in the score, and your serve is rated on its own rather than buried in an overall feeling. When you look back, you see which rounds you closed out and which ones got away.",
    stepperHeadline: 'From first round to final — and the season around it',
    season: {
      lead: 'A summer swing of tournaments, or a full year of club tennis — your season starts and ends where you say.',
      text: 'Give it a name and a goal worth chasing: a quarterfinal at the club championship, a first serve you can lean on at 4–4. Everything you log in that window counts — the ranking-tournament match and the Tuesday doubles night alike.',
      items: [
        'Shape it around your calendar — a clay block, the summer, the whole year',
        'Break the goal into milestones: a result, a stroke, or something else that matters to you',
        'Look at this season, last season or every season — it opens on the one you’re in',
      ],
      example: {
        name: 'Summer 2026 — club & ITF seniors',
        range: 'May 1 – Sep 15',
        goal: 'Quarterfinal at the club championship',
        milestones: [
          { t: 'First-serve rating ★4 average', s: 'achieved' },
          { t: 'Win a round at the regional open', s: 'active' },
        ],
      },
      previous: {
        name: 'Summer 2025 — club season',
        range: 'May 5 – Sep 14, 2025',
        goal: 'Win two rounds at the club championship',
        milestones: [
          { t: 'Reach the club championship R16', s: 'achieved' },
          { t: 'Win a third-set tiebreak', s: 'achieved' },
          { t: 'Backhand rating ★4 average', s: 'not_achieved' },
        ],
      },
    },
    competition: {
      lead: 'Read a tournament the way you lived it — round by round, from the first ball to the last.',
      text: 'Tag a match “R16” or “Final” and the whole draw lines up in order. Each tournament and ladder keeps its own win/loss, so you can see whether your tournament tennis holds up against the matches that count for less.',
      items: [
        'The draw in order, first round to final',
        'A win/loss record for that event alone',
        'Goal badge ✓ / ✗ once you’ve reviewed it — days remaining while it’s still on',
      ],
      example: {
        name: 'Club Championship',
        type: 'Tournament',
        record: '3–1',
        badge: 'achieved',
        rounds: [
          { r: 'R32', o: 'vs. M. Laine', w: true, sc: '6–3 6–4' },
          { r: 'R16', o: 'vs. J. Koski', w: true, sc: '7–6 3–6 10–7' },
          { r: 'QF', o: 'vs. T. Aho', w: true, sc: '6–4 6–2' },
          { r: 'SF', o: 'vs. P. Niemi', w: false, sc: '4–6 5–7' },
        ],
      },
    },
    matchCycle: {
      gamePlanLead: 'Know what you’re going to do with your serve before the warm-up ends.',
      gamePlanText: 'A few lines beforehand — where you want to serve, how you’ll handle their backhand. Afterwards, the score set by set and your own read on what decided it, while it’s still fresh.',
      before: ['Opponent, level, and the round it belongs to', 'A short written plan — the two or three things you’re playing for today'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Full set-by-set score, tiebreaks included',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    recordHeadline: 'A season of tennis matches, finally in one place.',
    quote: '"Matches logged inside a Competition average half a point higher on plan adherence than your standalone matches — tournament stakes seem to sharpen your preparation rather than loosen it."',
  },
  {
    slug: 'padel',
    heroImage: 'https://images.unsplash.com/photo-1767128890583-b3f8dc30bdbc?auto=format&fit=crop&w=1920&q=80',
    heroPosition: 'center 35%',
    value: 'padel',
    name: 'Padel',
    emoji: '🏸',
    doubles: true,
    tagline: 'In padel you never win alone. Keep a record that knows who was on your side of the glass.',
    body: "ZenAnalytics gives padel players a record that fits the game, because in padel the partnership is half the story. The same player can look like a different one next to someone else — steadier on the left with one partner, more aggressive at the net with another. So every match logs who you played with as well as the pair you faced. Club league, open tournaments and the Friday fourball all count toward the same season, and the record follows you, whoever you're partnered with this month.",
    stepperHeadline: 'One season, many partners — every match counted',
    season: {
      lead: 'One season, however many partners — the record follows you, not the pairing.',
      text: 'Make it your club’s winter league, an autumn of open tournaments or the whole year. Set what you’re playing for: a top-three finish, a partnership that clicks, a bandeja you trust. The friendly Friday fourball counts too.',
      items: [
        'Shape it around your calendar — a league run, half a year, the whole year',
        'Milestones for a result, a partnership target, or a shot to sharpen',
        'Look at this season, last season or every season — it opens on the one you’re in',
      ],
      example: {
        name: 'Winter league 2026–27',
        range: 'Oct 1 – Mar 31',
        goal: 'Finish top 3 in division 2',
        milestones: [
          { t: 'Net play ★4 with a regular partner', s: 'active' },
          { t: 'Reach a tournament semifinal', s: 'achieved' },
        ],
      },
      previous: {
        name: 'Winter league 2025–26',
        range: 'Oct 1, 2025 – Mar 31, 2026',
        goal: 'Promotion out of division 3',
        milestones: [
          { t: 'Finish top 2 in division 3', s: 'achieved' },
          { t: 'Settle on a regular partner', s: 'achieved' },
          { t: 'Bandeja ★4 average', s: 'not_achieved' },
        ],
      },
    },
    competition: {
      lead: 'League week by league week, with your partner beside every result.',
      text: 'Tag each match “Week 5”, “Group B” or “Semifinal” and the competition reads as one run. Its own record shows how this league is going for the two of you — not blurred into everything else you played.',
      items: [
        'League weeks and knockout rounds in order',
        'A win/loss record for that competition alone',
        'Goal badge ✓ / ✗ once reviewed — days remaining while it’s still on',
      ],
      example: {
        name: 'Club Winter League · Div 2',
        type: 'League',
        record: '4–2',
        daysLeft: 74,
        rounds: [
          { r: 'Week 3', o: 'vs. Lehto / Salo', w: true, sc: '6–4 6–3' },
          { r: 'Week 4', o: 'vs. Mäki / Ruus', w: false, sc: '4–6 6–7' },
          { r: 'Week 5', o: 'vs. Vainio / Ek', w: true, sc: '6–2 3–6 10–8' },
        ],
      },
    },
    matchCycle: {
      gamePlanLead: 'Agree the plan with your partner before the first serve, not at the first changeover.',
      gamePlanText: 'Write down what the two of you are going for — who takes the middle, when to go up for the net. Afterwards, the score and your own notes on how the partnership worked today.',
      before: ['Your partner, the opposing pair, and the round', 'A short written plan for the two of you'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Sets with super-tiebreak support',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    recordHeadline: 'Every padel match, every partner — one record that adds up.',
    quote: '"With this partner specifically, your Competition matches average a full star higher on net play than your standalone matches — the two of you seem to lift each other under pressure."',
  },
  {
    slug: 'squash',
    heroImage: 'https://images.unsplash.com/photo-1740813416102-5d42f408bc85?auto=format&fit=crop&w=1920&q=80',
    heroPosition: 'center 55%',
    value: 'squash',
    name: 'Squash',
    emoji: '🟡',
    doubles: false,
    tagline: 'Box by box, game by game to 11 — a squash record as relentless as the sport.',
    body: "ZenAnalytics gives competitive and club squash players a record built the way squash is actually organised, because most of us live in boxes and on ladders, not just in one-off matches. Your box league becomes a competition with its own record, so you can see exactly how this cycle went and whether you're moving up. Every match inside it is logged game by game to 11, and your short game is rated separately from your length — so the record shows whether the drops were landing or just the rallies were long.",
    stepperHeadline: 'From the season down to every game to 11',
    season: {
      lead: 'A box cycle, a winter of tournaments, a full year — you draw the lines.',
      text: 'Set what you’re chasing: up two boxes by spring, a plate final, length that keeps your opponent off the T. Everything you play in that window counts — box matches, tournament rounds and the friendly games in between.',
      items: [
        'Shape it around your calendar — one box cycle, a winter, the whole year',
        'Milestones for a result, a box position, or a part of your game',
        'Look at this season, last season or every season — it opens on the one you’re in',
      ],
      example: {
        name: '2026–27 box league season',
        range: 'Sep 1 – May 31',
        goal: 'Move up from box 4 to box 2',
        milestones: [
          { t: 'Win box 4 this cycle', s: 'achieved' },
          { t: 'Short game ★4 average', s: 'active' },
        ],
      },
      previous: {
        name: '2025–26 box league season',
        range: 'Sep 1, 2025 – May 31, 2026',
        goal: 'Move up from box 6 to box 4',
        milestones: [
          { t: 'Win box 6', s: 'achieved' },
          { t: 'Top 2 in box 5', s: 'achieved' },
          { t: 'Win a five-gamer', s: 'achieved' },
        ],
      },
    },
    competition: {
      lead: 'Every box cycle gets its own record — so you know exactly where you stand.',
      text: 'Label matches “Box round 3” or “Plate final” and the cycle reads as one sequence. You see how this box went on its own, with days left in the cycle while there are still matches to play.',
      items: [
        'Every match in the box, in order',
        'A win/loss record for that box or tournament alone',
        'Goal badge ✓ / ✗ once reviewed — days remaining while a cycle is still on',
      ],
      example: {
        name: 'Box 4 · Autumn cycle',
        type: 'League',
        record: '3–1',
        daysLeft: 12,
        rounds: [
          { r: 'Round 1', o: 'vs. A. Rantala', w: true, sc: '3–1' },
          { r: 'Round 2', o: 'vs. K. Heino', w: true, sc: '3–2' },
          { r: 'Round 3', o: 'vs. S. Lind', w: false, sc: '1–3' },
          { r: 'Round 4', o: 'vs. O. Pitkä', w: true, sc: '3–0' },
        ],
      },
    },
    matchCycle: {
      gamePlanLead: 'Step on court knowing your plan — length first, or straight to the front?',
      gamePlanText: 'Write a line or two before you warm the ball up: how you’ll pin them in the back corners, when to go short. Afterwards, the scores game by game and your own account of who controlled the T.',
      before: ['Opponent, level, and the box or round', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Games to 11, best of 5',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and short game, rated individually',
      ],
    },
    recordHeadline: 'Box after box, your squash adds up to a picture.',
    quote: '"Your plan adherence rating and your win rate move together across logged matches — the games you rated highest on holding your length are also your highest-rated performances."',
  },
  {
    slug: 'badminton',
    heroImage: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?auto=format&fit=crop&w=1920&q=80',
    heroPosition: 'center 40%',
    value: 'badminton',
    name: 'Badminton',
    emoji: '🏸',
    doubles: true,
    tagline: 'Singles, doubles and the club league in one season — and the margins of every set to 21 on record.',
    body: "ZenAnalytics gives badminton players a record that keeps up with how they actually compete, because a badminton season rarely means one thing. You might play singles in the morning and doubles in the afternoon at the same tournament, with league fixtures in between. Sets to 21 are often decided by a few points, so the detail matters: every match is logged with its full score, your partner when there is one, and separate ratings for serve, net play and the rest of your game. Over a season, you can see where those close sets actually went.",
    stepperHeadline: 'Singles and doubles, one season, every set to 21',
    season: {
      lead: 'A season that holds your singles and your doubles side by side.',
      text: 'Fit it to your calendar — the autumn tournament series, a league year, all twelve months. Set the goal: a final in your doubles event, a round won in singles, a low serve you trust at 19–19. Every match in that window counts, competition or not.',
      items: [
        'Shape it around your calendar — a tournament block, a league year, the whole year',
        'Milestones for a result, a part of your game, or something else',
        'Look at this season, last season or every season — it opens on the one you’re in',
      ],
      example: {
        name: 'Autumn 2026 — national series',
        range: 'Aug 15 – Dec 20',
        goal: 'Reach a men’s doubles final',
        milestones: [
          { t: 'Serve ★4 average in doubles', s: 'achieved' },
          { t: 'Win a singles round at nationals', s: 'not_achieved' },
        ],
      },
      previous: {
        name: 'League year 2025–26',
        range: 'Sep 1, 2025 – May 15, 2026',
        goal: 'Win half of my league doubles matches',
        milestones: [
          { t: '10 league doubles wins', s: 'achieved' },
          { t: 'Net play ★4 average', s: 'achieved' },
          { t: 'Win a deciding set to 21', s: 'not_achieved' },
        ],
      },
    },
    competition: {
      lead: 'Each event at a tournament weekend kept as its own run — group stage to final.',
      text: 'Tag matches “Group stage”, “QF” or “Match 6”, and your men’s doubles and your singles draw line up separately instead of as one pile of scorelines. Each keeps its own record.',
      items: [
        'Group stage through final, in order',
        'A win/loss record for that event alone',
        'Goal badge ✓ / ✗ once reviewed — days remaining while it’s still on',
      ],
      example: {
        name: 'Regional Open · MD',
        type: 'Tournament',
        record: '2–1',
        badge: 'not_achieved',
        rounds: [
          { r: 'R16', o: 'vs. Berg / Holm', w: true, sc: '21–17 21–15' },
          { r: 'QF', o: 'vs. Tan / Lee', w: true, sc: '19–21 21–18 21–16' },
          { r: 'SF', o: 'vs. Nyberg / Sund', w: false, sc: '15–21 18–21' },
        ],
      },
    },
    matchCycle: {
      gamePlanLead: 'Rallies are over in seconds — the plan has to be clear before the first serve.',
      gamePlanText: 'A few lines beforehand: where to serve, who covers the front in doubles, how to move them in singles. Afterwards, every set to 21 and your own notes on what the close points came down to.',
      before: ['Opponent (and partner, in doubles), level and round', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Up to 3 sets to 21',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    recordHeadline: 'Every set to 21, singles and doubles, building one picture.',
    quote: '"Your highest performance ratings this season all follow a match where a plan was written beforehand — your last two matches were logged without one."',
  },
  {
    slug: 'pickleball',
    heroImage: 'https://images.unsplash.com/photo-1711996151738-657bd95b7cdb?auto=format&fit=crop&w=1920&q=80',
    heroPosition: 'center 60%',
    value: 'pickleball',
    name: 'Pickleball',
    emoji: '🎾',
    doubles: true,
    tagline: 'From open play to the gold bracket — a pickleball record that grows as fast as your game.',
    body: "ZenAnalytics gives pickleball players — whether you're climbing toward your next DUPR level or just playing open play four nights a week — a record that matches how the sport is played. A season can mean rec sessions, a ladder night and a couple of tournament weekends in the same month, mixed doubles one day and gender doubles the next. Every match gets its place: pool play and brackets grouped by event, the score of every game to 11, and net play rated separately so you can see how your kitchen-line game is holding up over time.",
    stepperHeadline: 'Open play, ladders, brackets — one season',
    season: {
      lead: 'From open play to tournament weekends — one season, every game counted.',
      text: 'Set the window yourself — a spring tournament swing, a summer, the full year — and the goal you’re chasing: a medal at your local event, the next DUPR bracket, a third-shot drop you can rely on. Open play logged in that range counts too.',
      items: [
        'Shape it around your calendar — a tournament swing, a summer, the whole year',
        'Milestones for a result, a rating bracket, or a shot to own',
        'Look at this season, last season or every season — it opens on the one you’re in',
      ],
      example: {
        name: 'Spring swing 2026',
        range: 'Mar 1 – Jun 30',
        goal: 'Medal in 4.0 mixed doubles',
        milestones: [
          { t: 'Net play ★4 at the kitchen line', s: 'active' },
          { t: 'Play 3 sanctioned events', s: 'achieved' },
        ],
      },
      previous: {
        name: 'Fall 2025 — first tournaments',
        range: 'Sep 1 – Dec 15, 2025',
        goal: 'Play my first bracket at 3.5',
        milestones: [
          { t: 'Enter a 3.5 doubles bracket', s: 'achieved' },
          { t: 'Win a pool play match', s: 'achieved' },
          { t: 'Third-shot drop ★4 average', s: 'not_achieved' },
        ],
      },
    },
    competition: {
      lead: 'Pool play, brackets and ladder nights — each event its own story.',
      text: 'Label matches “Pool A”, “Gold bracket SF” or “Ladder week 4” and a whole event reads as one sequence. Its own record shows how that tournament went, apart from the open play around it.',
      items: [
        'Pool play through medal match, in order',
        'A win/loss record for that event alone',
        'Goal badge ✓ / ✗ once reviewed — days remaining while it’s still on',
      ],
      example: {
        name: 'City Open · 4.0 Mixed',
        type: 'Tournament',
        record: '4–1',
        badge: 'achieved',
        rounds: [
          { r: 'Pool A', o: 'vs. Reed / Cole', w: true, sc: '11–7' },
          { r: 'Pool A', o: 'vs. Park / Diaz', w: false, sc: '9–11' },
          { r: 'Gold SF', o: 'vs. Shaw / Kim', w: true, sc: '11–8 11–9' },
          { r: 'Gold F', o: 'vs. Ford / Lane', w: true, sc: '11–6 8–11 11–9' },
        ],
      },
    },
    matchCycle: {
      gamePlanLead: 'Decide how you’re getting to the kitchen line before the first serve.',
      gamePlanText: 'A line or two beforehand — drop or drive on the third shot, who takes the middle. Afterwards, every game’s score and your own notes on how the dink battles went.',
      before: ['Opponent(s), level and round', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Up to 3 sets to 11',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    recordHeadline: 'All that pickleball you play, turned into a record you can read.',
    quote: '"Matches logged with a written plan beforehand average a full star higher on performance than the ones logged without one — the pattern has held for six weeks."',
  },
  {
    slug: 'table-tennis',
    heroImage: 'https://images.unsplash.com/photo-1786025106105-88a13e54f548?auto=format&fit=crop&w=1920&q=80',
    heroPosition: 'center 45%',
    value: 'table_tennis',
    name: 'Table Tennis',
    emoji: '🏓',
    doubles: true,
    tagline: 'Three or four matches every league night. Finally, a record that keeps up.',
    body: "ZenAnalytics gives table tennis players a record built for the volume they actually play, because few racket sports pack in as many matches. One league evening can mean three or four matches back to back, and by the next match day they've blurred together. Here every match day stays grouped in order, every game to 11 is in the score, and your serve and short game are rated separately — in a sport where points are won and lost in the first two or three balls, that's where the record looks closest.",
    stepperHeadline: 'League night by league night, match by match',
    season: {
      lead: 'A league half, a full league year — and every match in between.',
      text: 'Match it to your club’s calendar and set the goal: promotion, a rating milestone, a serve you want to own. Every match logged in that span counts — league fixtures, tournament groups and club nights.',
      items: [
        'Shape it around your calendar — a league half, a league year, the whole year',
        'Milestones for a result, a rating target, or a part of your game',
        'Look at this season, last season or every season — it opens on the one you’re in',
      ],
      example: {
        name: 'League year 2026–27',
        range: 'Aug 20 – Apr 30',
        goal: 'Promotion to division 1',
        milestones: [
          { t: 'Serve ★4 average', s: 'active' },
          { t: 'Win 60% of league matches by January', s: 'active' },
        ],
      },
      previous: {
        name: 'League year 2025–26',
        range: 'Aug 20, 2025 – Apr 30, 2026',
        goal: 'Stay up in division 2',
        milestones: [
          { t: 'Finish mid-table or higher', s: 'achieved' },
          { t: 'Short game ★4 average', s: 'achieved' },
          { t: 'Beat a top-3 player', s: 'not_achieved' },
        ],
      },
    },
    competition: {
      lead: 'A busy league night stays one block — not a stack of scorelines.',
      text: 'Tag each match “Match day 7” or “Group 3” and everything you played that evening sits together, in sequence, under its league. Each league or tournament keeps its own record.',
      items: [
        'Every match day in sequence, matches grouped together',
        'A win/loss record for that league or tournament alone',
        'Goal badge ✓ / ✗ once reviewed — days remaining while it’s still on',
      ],
      example: {
        name: 'Club League · Div 2',
        type: 'League',
        record: '9–5',
        daysLeft: 117,
        rounds: [
          { r: 'Match day 6', o: 'vs. E. Kallio', w: true, sc: '3–1' },
          { r: 'Match day 6', o: 'vs. H. Vuori', w: false, sc: '2–3' },
          { r: 'Match day 7', o: 'vs. R. Sato', w: true, sc: '3–0' },
        ],
      },
    },
    matchCycle: {
      gamePlanLead: 'Between matches on a league night, a few lines are all the plan you need.',
      gamePlanText: 'Note which serves you’ll open with and how you’ll handle their receive. Afterwards, every game to 11 and your own thoughts on serve, return and the short game while they’re still sharp.',
      before: ['Opponent, level and round', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Up to 7 sets to 11',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and short game, rated individually',
      ],
    },
    recordHeadline: 'All those table tennis matches, adding up to a pattern you can see.',
    quote: '"Across your logged matches, your performance rating against defensive-style opponents sits a full star below your average against everyone else."',
  },
];

// Back-compat: layerContent.js (homepage LayeredExplainer demo) still reads
// sport.stages[0|1].items — derive it from the richer fields above.
SPORTS.forEach((s) => {
  s.stages = [
    { title: 'Season', color: 'accent', items: s.season.items },
    { title: 'Competitions', color: 'purple', items: s.competition.items },
  ];
});

export function getSportBySlug(slug) {
  return SPORTS.find((s) => s.slug === slug) || null;
}
